import { useState } from "react";
import { Card } from "@remo-co/ui-core/src/components/Card";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { CardHeader } from "@remo-co/ui-core/src/components/CardHeader";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { IconButtonWithTooltip } from "@remo-co/ui-core/src/components/IconButtonWithTooltip";
import { useI18n } from "i18n";
import { Delete as DeleteIcon } from "@remo-co/ui-core/src/icons/Delete";
import { Edit as EditIcon } from "@remo-co/ui-core/src/icons/Edit";
import { Link02 as Link02Icon } from "@remo-co/ui-core/src/icons/Link02";
import { selectIsMobile } from "modules/deviceInfo";
import { useSelector } from "react-redux";
import {
  addDialogNotification,
  updateDialogNotificationParams,
} from "modules/dialogNotification/redux/dialogNotificationSlice";
import { useAppDispatch } from "store/hooks";
import { Building05 as Building05Icon } from "@remo-co/ui-core/src/icons/Building05";
import useCompanyEventDirectory from "../hooks/useCompanyEventDirectory";
import { EVENT_DIRECTORY_DELETE_CONFIRM_DIALOG } from "../constants";
import { useStyles } from "./EventDirectoryItem.styles";
import EventDirectoryDialog from "./EventDirectoryDialog";
import { IEventDirectory } from "../types";

export interface IEventDirectoryItemProps {
  eventDirectory: IEventDirectory;
}

const EventDirectoryItem = ({ eventDirectory }: IEventDirectoryItemProps) => {
  const styles = useStyles();
  const dispatch = useAppDispatch();
  const isMobileView = useSelector(selectIsMobile);
  const { t } = useI18n(["common", "eventDirectory"]);
  const { deleteCompanyEventDirectory } = useCompanyEventDirectory();
  const [isDialogOpen, setDialogOpen] = useState(false);

  const onItemEdit = () => {
    setDialogOpen(true);
  };
  const onDialogClose = () => {
    setDialogOpen(false);
  };
  const onDeleteConfirmation = async () => {
    dispatch(
      updateDialogNotificationParams({
        type: EVENT_DIRECTORY_DELETE_CONFIRM_DIALOG,
        data: {
          dismissDisabled: true,
          confirmLoading: true,
        },
      }),
    );

    await deleteCompanyEventDirectory(eventDirectory.id);
  };
  const onItemDelete = () => {
    dispatch(
      addDialogNotification({
        title: t("common:are.you.sure"),
        message: t("eventDirectory:delete.ask.to.confirm"),
        hideCloseButton: true,
        type: EVENT_DIRECTORY_DELETE_CONFIRM_DIALOG,
        confirmText: t("eventDirectory:delete.confirm"),
        onConfirm: onDeleteConfirmation,
        dismissText: t("eventDirectory:delete.cancel"),
        confirmTestId: "confirm-delete-event-directory",
      }),
    );
  };

  return (
    <Card size="lg" flex={false} fullWidth={false} className={styles.item}>
      <CardHeader
        classes={{
          root: styles.cardHeaderRoot,
          actions: styles.cardHeaderActions,
        }}
        action={
          !isMobileView ? (
            <div className={styles.menu}>
              <IconButtonWithTooltip
                className={styles.menuItem}
                id="event-directory-edit-btn"
                title={t("common:button.edit")}
                aria-label={t("common:button.edit")}
                data-testid="event-directory-item-update"
                onClick={onItemEdit}
              >
                <EditIcon className={styles.menuItemSVG} />
              </IconButtonWithTooltip>
              <IconButtonWithTooltip
                className={styles.menuItem}
                id="event-directory-delete-btn"
                title={t("common:button.delete")}
                aria-label={t("common:button.delete")}
                data-testid="event-directory-item-delete"
                onClick={onItemDelete}
              >
                <DeleteIcon className={styles.menuItemSVG} />
              </IconButtonWithTooltip>
            </div>
          ) : undefined
        }
      />
      <Container
        className={styles.innerContainer}
        onClick={!isMobileView ? onItemEdit : undefined}
        data-testid="event-directory-item-container"
      >
        <Container className={styles.iconContainer}>
          <Building05Icon />
        </Container>
        <Container>
          <Typography variant="h4" className={styles.name}>
            {eventDirectory.name}
          </Typography>
          <Typography variant="body1" className={styles.numberOfEvents}>
            <Link02Icon className={styles.linkIcon} />
            <span>
              {eventDirectory.events.length} {t("eventDirectory:events.linked")}
            </span>
          </Typography>
        </Container>
      </Container>
      {isDialogOpen && (
        <EventDirectoryDialog
          handleClose={onDialogClose}
          directory={eventDirectory}
        />
      )}
    </Card>
  );
};

export default EventDirectoryItem;
